import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

type Role = 'buyer' | 'seller' | 'both';
type Frequency = 'daily' | 'few_times_week' | 'few_times_month' | 'rarely';
type BuyCategory = 'clothing' | 'electronics' | 'food' | 'household' | 'collectibles' | 'other';
type Delivery = 'very' | 'somewhat' | 'not' | 'prefer_pickup';
type Feature =
  | 'realtime_tracking'
  | 'secure_payments'
  | 'verified_sellers'
  | 'seller_ratings'
  | 'fast_delivery'
  | 'buyer_protection';
type Trust =
  | 'verified_identity'
  | 'ratings_reviews'
  | 'completed_deliveries'
  | 'realtime_tracking'
  | 'buyer_protection'
  | 'all';
type WouldSell = 'yes' | 'maybe' | 'no';
type SellReason =
  | 'more_local_buyers'
  | 'easy_listing'
  | 'secure_payments'
  | 'delivery_tools'
  | 'realtime_tracking'
  | 'low_fees'
  | 'seller_verification';
type NotUse =
  | 'delivery_fees'
  | 'not_enough_sellers'
  | 'not_enough_buyers'
  | 'trust_safety'
  | 'prefer_other'
  | 'other';
type Likelihood = 'very_likely' | 'likely' | 'maybe' | 'unlikely' | 'definitely_not';
type YesNo = 'yes' | 'no';
type SiteIssue = 'not_used' | 'fine' | 'confusing' | 'broken' | 'other';

function toggleValue<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function isValidEmail(value: string): boolean {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

const optionClass =
  'flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 cursor-pointer hover:border-gray-300';
const inputClass =
  'mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-green-600 focus:ring-green-500';
const labelClass = 'text-sm text-gray-800';
const fieldClass = 'mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500';

const Survey: React.FC = () => {
  const [role, setRole] = useState<Role | ''>('');
  const [marketplaceFrequency, setMarketplaceFrequency] = useState<Frequency | ''>('');
  const [buyCategory, setBuyCategory] = useState<BuyCategory | ''>('');
  const [buyCategoryOther, setBuyCategoryOther] = useState('');
  const [localDeliveryImportance, setLocalDeliveryImportance] = useState<Delivery | ''>('');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [trustFactor, setTrustFactor] = useState<Trust | ''>('');
  const [wouldSell, setWouldSell] = useState<WouldSell | ''>('');
  const [sellReasons, setSellReasons] = useState<SellReason[]>([]);
  const [notUseReason, setNotUseReason] = useState<NotUse | ''>('');
  const [notUseOther, setNotUseOther] = useState('');
  const [siteIssue, setSiteIssue] = useState<SiteIssue | ''>('');
  const [issueDetails, setIssueDetails] = useState('');
  const [likelihood, setLikelihood] = useState<Likelihood | ''>('');
  const [changeRequest, setChangeRequest] = useState('');
  const [wantUpdates, setWantUpdates] = useState<YesNo | ''>('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'User survey — BuyDrop';
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return;
      const user = JSON.parse(raw) as { email?: string };
      if (user?.email) setEmail(user.email);
    } catch {
      /* ignore */
    }
  }, []);

  const emailNeeded = wantUpdates === 'yes';
  const emailOk = !emailNeeded || isValidEmail(email);
  const canSubmit = !!role && !!likelihood && emailOk && !submitting;

  const submitHint = useMemo(() => {
    if (!role) return 'Choose what best describes you.';
    if (!likelihood) return 'Choose how likely you are to use BuyDrop.';
    if (!emailOk) return 'Enter an email so we can send updates.';
    return '';
  }, [role, likelihood, emailOk]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(submitHint);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/survey', {
        role,
        marketplaceFrequency: marketplaceFrequency || undefined,
        buyCategory: buyCategory || undefined,
        buyCategoryOther: buyCategory === 'other' ? buyCategoryOther.trim() : undefined,
        localDeliveryImportance: localDeliveryImportance || undefined,
        features,
        trustFactor: trustFactor || undefined,
        wouldSell: wouldSell || undefined,
        sellReasons,
        notUseReason: notUseReason || undefined,
        notUseOther: notUseReason === 'other' ? notUseOther.trim() : undefined,
        siteIssue: siteIssue || undefined,
        issueDetails:
          siteIssue && siteIssue !== 'not_used' && siteIssue !== 'fine'
            ? issueDetails.trim() || undefined
            : undefined,
        likelihood,
        changeRequest: changeRequest.trim() || undefined,
        wantUpdates: wantUpdates || undefined,
        email: email.trim() || undefined,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not save the survey. Try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const showBuying = role === 'buyer' || role === 'both';
  const showSelling = role === 'seller' || role === 'both';
  const showWouldSell = role === 'buyer';
  const showIssueDetails = siteIssue === 'confusing' || siteIssue === 'broken' || siteIssue === 'other';
  let qn = 1;
  const next = () => qn++;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 sm:py-14">
        <div className="container mx-auto max-w-xl px-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-10">
            <h1 className="text-2xl font-bold text-gray-900">Thanks for the feedback</h1>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">
              Your answers help us decide what to improve next on BuyDrop.
            </p>
            <Link to="/" className="mt-6 inline-block text-sm font-medium text-green-700 hover:underline">
              Back to BuyDrop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 sm:py-14">
      <div className="container mx-auto max-w-xl px-4">
        <nav className="mb-6 text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/" className="font-medium text-green-700 hover:text-green-800">
            Home
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-700">Survey</span>
        </nav>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-10">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">BuyDrop user survey</h1>
          <p className="mt-3 text-sm text-gray-600 leading-relaxed">
            Help us make BuyDrop better. This survey takes about 2 minutes. Skip anything you do not want to answer.
          </p>

          <Section title="About you">
            <Question n={next()} label="What best describes you?" required>
              <ChoiceList
                name="role"
                value={role}
                onChange={setRole}
                options={[
                  { value: 'buyer', label: 'Buyer' },
                  { value: 'seller', label: 'Seller' },
                  { value: 'both', label: 'Both buyer and seller' },
                ]}
              />
            </Question>
            <Question n={next()} label="How often do you use online marketplaces?">
              <ChoiceList
                name="frequency"
                value={marketplaceFrequency}
                onChange={setMarketplaceFrequency}
                options={[
                  { value: 'daily', label: 'Daily' },
                  { value: 'few_times_week', label: 'A few times a week' },
                  { value: 'few_times_month', label: 'A few times a month' },
                  { value: 'rarely', label: 'Rarely' },
                ]}
              />
            </Question>
            <Question n={next()} label="Did anything get in the way when you used BuyDrop?">
              <ChoiceList
                name="siteIssue"
                value={siteIssue}
                onChange={setSiteIssue}
                options={[
                  { value: 'not_used', label: 'I have not used it yet' },
                  { value: 'fine', label: 'No, it was fine' },
                  { value: 'confusing', label: 'Something was confusing' },
                  { value: 'broken', label: 'Something did not work' },
                  { value: 'other', label: 'Other' },
                ]}
              />
              {showIssueDetails && (
                <textarea
                  className={`${fieldClass} min-h-[80px] resize-y`}
                  placeholder="What happened?"
                  value={issueDetails}
                  onChange={(e) => setIssueDetails(e.target.value)}
                  maxLength={1000}
                  rows={3}
                />
              )}
            </Question>
          </Section>

          {showBuying && (
            <Section title="Buying on BuyDrop">
              <Question n={next()} label="What would you most likely buy on BuyDrop?">
                <ChoiceList
                  name="buyCategory"
                  value={buyCategory}
                  onChange={setBuyCategory}
                  options={[
                    { value: 'clothing', label: 'Clothing & shoes' },
                    { value: 'electronics', label: 'Electronics' },
                    { value: 'food', label: 'Food' },
                    { value: 'household', label: 'Household items' },
                    { value: 'collectibles', label: 'Collectibles' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
                {buyCategory === 'other' && (
                  <input
                    type="text"
                    className={fieldClass}
                    placeholder="What would you buy?"
                    value={buyCategoryOther}
                    onChange={(e) => setBuyCategoryOther(e.target.value)}
                    maxLength={120}
                  />
                )}
              </Question>
              <Question n={next()} label="How important is local delivery to you?">
                <ChoiceList
                  name="delivery"
                  value={localDeliveryImportance}
                  onChange={setLocalDeliveryImportance}
                  options={[
                    { value: 'very', label: 'Very important' },
                    { value: 'somewhat', label: 'Somewhat important' },
                    { value: 'not', label: 'Not important' },
                    { value: 'prefer_pickup', label: 'I prefer pickup' },
                  ]}
                />
              </Question>
              <Question n={next()} label="Which BuyDrop features matter most to you?" hint="Select all that apply.">
                <CheckList
                  values={features}
                  onToggle={(v) => setFeatures((prev) => toggleValue(prev, v))}
                  options={[
                    { value: 'realtime_tracking', label: 'Real-time delivery tracking' },
                    { value: 'secure_payments', label: 'Secure in-app payments' },
                    { value: 'verified_sellers', label: 'Verified sellers' },
                    { value: 'seller_ratings', label: 'Seller ratings' },
                    { value: 'fast_delivery', label: 'Fast delivery' },
                    { value: 'buyer_protection', label: 'Buyer protection' },
                  ]}
                />
              </Question>
              <Question n={next()} label="What would make you trust a seller enough to place an order?">
                <ChoiceList
                  name="trust"
                  value={trustFactor}
                  onChange={setTrustFactor}
                  options={[
                    { value: 'verified_identity', label: 'Verified identity' },
                    { value: 'ratings_reviews', label: 'Good ratings/reviews' },
                    { value: 'completed_deliveries', label: 'Number of completed deliveries' },
                    { value: 'realtime_tracking', label: 'Real-time tracking' },
                    { value: 'buyer_protection', label: 'Buyer protection' },
                    { value: 'all', label: 'All of these' },
                  ]}
                />
              </Question>
            </Section>
          )}

          {(showWouldSell || showSelling) && (
            <Section title="Selling on BuyDrop">
              {showWouldSell && (
                <Question n={next()} label="Would you consider selling on BuyDrop?">
                  <ChoiceList
                    name="wouldSell"
                    value={wouldSell}
                    onChange={setWouldSell}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'maybe', label: 'Maybe' },
                      { value: 'no', label: 'No' },
                    ]}
                  />
                </Question>
              )}
              {showSelling && (
                <Question n={next()} label="What matters most when you sell on BuyDrop?" hint="Select all that apply.">
                  <CheckList
                    values={sellReasons}
                    onToggle={(v) => setSellReasons((prev) => toggleValue(prev, v))}
                    options={[
                      { value: 'more_local_buyers', label: 'More local buyers' },
                      { value: 'easy_listing', label: 'Easy listing' },
                      { value: 'secure_payments', label: 'Secure payments' },
                      { value: 'delivery_tools', label: 'Delivery tools' },
                      { value: 'realtime_tracking', label: 'Real-time tracking' },
                      { value: 'low_fees', label: 'Low seller fees' },
                      { value: 'seller_verification', label: 'Seller verification' },
                    ]}
                  />
                </Question>
              )}
            </Section>
          )}

          <Section title="Your opinion">
            <Question n={next()} label="What is the biggest reason you would not use BuyDrop?">
              <ChoiceList
                name="notUse"
                value={notUseReason}
                onChange={setNotUseReason}
                options={[
                  { value: 'delivery_fees', label: 'Delivery fees' },
                  { value: 'not_enough_sellers', label: 'Not enough sellers' },
                  { value: 'not_enough_buyers', label: 'Not enough buyers' },
                  { value: 'trust_safety', label: 'Trust/safety concerns' },
                  { value: 'prefer_other', label: 'I prefer another marketplace' },
                  { value: 'other', label: 'Other' },
                ]}
              />
              {notUseReason === 'other' && (
                <input
                  type="text"
                  className={fieldClass}
                  placeholder="What would stop you?"
                  value={notUseOther}
                  onChange={(e) => setNotUseOther(e.target.value)}
                  maxLength={200}
                />
              )}
            </Question>
            <Question n={next()} label="How likely are you to use BuyDrop?" required>
              <ChoiceList
                name="likelihood"
                value={likelihood}
                onChange={setLikelihood}
                options={[
                  { value: 'very_likely', label: 'Very likely' },
                  { value: 'likely', label: 'Likely' },
                  { value: 'maybe', label: 'Maybe' },
                  { value: 'unlikely', label: 'Unlikely' },
                  { value: 'definitely_not', label: 'Definitely not' },
                ]}
              />
            </Question>
            <Question n={next()} label="What is one thing you would change or add to BuyDrop?">
              <textarea
                className={`${fieldClass} min-h-[96px] resize-y`}
                value={changeRequest}
                onChange={(e) => setChangeRequest(e.target.value)}
                maxLength={1000}
                rows={4}
              />
            </Question>
          </Section>

          <Section title="Stay updated">
            <Question n={next()} label="Want to hear when BuyDrop expands in your area?">
              <ChoiceList
                name="updates"
                value={wantUpdates}
                onChange={setWantUpdates}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
              />
            </Question>
            <div className="mt-4">
              <label htmlFor="survey-email" className="text-sm font-medium text-gray-900">
                Email
              </label>
              <input
                id="survey-email"
                type="email"
                className={fieldClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required={emailNeeded}
              />
            </div>
          </Section>

          {error && (
            <p className="mt-6 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          {!error && submitHint && (
            <p className="mt-6 text-sm text-gray-500">{submitHint}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-6 w-full rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Submit survey'}
          </button>
        </form>
      </div>
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10 border-t border-gray-200 pt-8">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <div className="mt-6 space-y-8">{children}</div>
    </section>
  );
}

function Question({
  n,
  label,
  hint,
  required,
  children,
}: {
  n: number;
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-900">
        {n}. {label}
        {required ? <span className="ml-1 text-gray-500 font-normal">Required</span> : null}
      </legend>
      {hint ? <p className="mt-1 text-sm text-gray-500">{hint}</p> : null}
      <div className="mt-3 space-y-2">{children}</div>
    </fieldset>
  );
}

function ChoiceList<T extends string>({
  name,
  value,
  onChange,
  options,
}: {
  name: string;
  value: T | '';
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <>
      {options.map((opt) => (
        <label key={opt.value} className={optionClass}>
          <input
            type="radio"
            className={inputClass}
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <span className={labelClass}>{opt.label}</span>
        </label>
      ))}
    </>
  );
}

function CheckList<T extends string>({
  values,
  onToggle,
  options,
}: {
  values: T[];
  onToggle: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <>
      {options.map((opt) => (
        <label key={opt.value} className={optionClass}>
          <input
            type="checkbox"
            className={inputClass}
            checked={values.includes(opt.value)}
            onChange={() => onToggle(opt.value)}
          />
          <span className={labelClass}>{opt.label}</span>
        </label>
      ))}
    </>
  );
}

export default Survey;
